import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared/components";
import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

import { ConnectorHostingWithServiceAccount } from "./connector-hosting.ts";
import { INVALID_ITEM_ID, isItemId } from "./item-id.ts";
import { INVALID_SECRET_NAME, isSecretName } from "./secret-name.ts";

/** Two items, so that one connector can serve several LaunchDarkly accounts
 * and be reinstalled without touching them.
 *
 * `connector` is one deployment of P0's LaunchDarkly connector: where it runs
 * and, from that, the identity it runs as. `account` is one LaunchDarkly
 * account: which connector reaches it, where the account is hosted, and
 * where the connector finds the account's access token.
 *
 * The `instance` option keys are the connector's own values for it. The
 * frontend bundles this package, so it can't import the connector: the
 * backend's `src/__tests__/connector-contract.test.ts` holds the keys to the
 * connector's schema.
 */
export const LaunchDarklyComponents = InstallSpec({
  connector: ItemComponent({
    description:
      "Runs P0's LaunchDarkly connector in your AWS account or Google Cloud project",
    label: "LaunchDarkly connector",
    maturity: "preview",
    schema: {
      // The variant that names the connector's identity after the connector,
      // so each account can grant that identity read on its secret.
      hosting: ConnectorHostingWithServiceAccount,
    },
    type: "string",
    // A connector's request holds only `hosting`, so the install framework
    // gives nothing away when it repeats the request beside a refused id.
    validator: async (id) => (isItemId(id) ? undefined : INVALID_ITEM_ID),
  }),
  account: ItemComponent({
    description:
      "A LaunchDarkly account whose existing members P0 can grant base roles to. Requests name it by its name here, as in acme-prod/writer.",
    label: "LaunchDarkly account",
    maturity: "preview",
    // No item validator, though an account's id has the connector's rule
    // (`isItemId`). The install framework answers a failing item validator
    // with the whole request, and logs it, and an account's request may hold
    // the access token pasted in place of its secret's name. So the account's
    // assembler has to check the id itself.
    //
    // Every field the form asks for is `step: "new"`, so a verify or a
    // configure that changes one is refused. A PUT isn't held to that: the
    // account's assembler has to refuse a PUT over an existing item. The
    // account's read grant names its connector's identity and its secret, so
    // a new connector, vault or secret takes a new item. So does a new
    // instance, since an open request holds a member's ID in one account:
    // remove the account and add it again once its live requests end.
    //
    // The form asks for the fields in this order.
    schema: {
      // A `connector` item's id.
      connector: Element({
        description:
          "The LaunchDarkly connector that reaches this account. It must run in the same cloud as the account's secret.",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      instance: Element({
        default: "commercial",
        description: "Where your LaunchDarkly account is hosted",
        label: "LaunchDarkly instance",
        options: {
          commercial: Option({
            label: "Commercial (app.launchdarkly.com)",
            maturity: "preview",
            schema: {},
          }),
          eu: Option({
            label: "EU (app.eu.launchdarkly.com)",
            maturity: "preview",
            schema: {},
          }),
          federal: Option({
            label: "Federal (app.launchdarkly.us)",
            maturity: "preview",
            schema: {},
          }),
        },
        step: "new",
        type: "select",
      }),
      vault: withoutAzureKeyVault(
        newVaultElement({ owner: "LaunchDarkly account" })
      ),
      tokenSecretName: Element({
        description:
          "The name, not the ARN or the full resource name, of the secret that holds the account's LaunchDarkly access token, in the secret manager above: an admin's personal access token, with the Admin role. Only your connector reads it, never P0.",
        label: "Access token secret",
        step: "new",
        type: "string",
        validator: async (_id, name) =>
          isSecretName(name) ? undefined : INVALID_SECRET_NAME,
      }),
      // P0 reads these from the connector each time it validates the
      // account. None can be typed in.
      accountId: Element({
        description:
          "The LaunchDarkly account the access token belongs to, read when an admin last checked the account",
        label: "LaunchDarkly account ID",
        type: "generated",
      }),
      tokenKind: Element({
        description:
          "Whether the access token is a service token or a member's personal token, read when an admin last checked the account",
        label: "Token kind",
        type: "generated",
      }),
      tokenOwnerMemberId: Element({
        description:
          "The LaunchDarkly member who owns a personal access token, read when an admin last checked the account. P0 never grants to this member. Empty for a service token.",
        label: "Token owner",
        type: "generated",
      }),
      allowedDomains: Element({
        description:
          "The email domains the connector grants to, as its DOMAIN_ALLOW_PATTERN read when an admin last checked the account. To change them, change the connector's deployment.",
        label: "Allowed domains",
        type: "generated",
      }),
    },
    type: "string",
  }),
});
